import { apiClient } from './apiClient';

export type ServiceProblem = {
  id: string;
  problemNumber: number;
  problemCode: string;
  title: string;
  description?: string | null;
  rootCause?: string | null;
  workaround?: string | null;
  status: string;
  priority: string;
  companyId?: string | null;
  companyName?: string | null;
  salesProductId?: string | null;
  salesProductName?: string | null;
  contractId?: string | null;
  contractCode?: string | null;
  assignedTeamId?: string | null;
  assignedTeamName?: string | null;
  assignedToUserId?: string | null;
  assignedToUserName?: string | null;
  planningWorkItemId?: string | null;
  linkedTicketCount: number;
  resolvedAtUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type ServiceProblemUpsertPayload = {
  title: string;
  description?: string;
  rootCause?: string;
  workaround?: string;
  status?: string;
  priority?: string;
  companyId?: string;
  salesProductId?: string;
  contractId?: string;
  assignedTeamId?: string;
  assignedToUserId?: string;
  planningWorkItemId?: string;
};

export type ServiceProblemListResult = {
  items: ServiceProblem[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type ServiceProblemTicketSummary = {
  id: string;
  ticketCode: string;
  title: string;
  status: string;
  priority: string;
  companyName: string;
  createdAtUtc: string;
};

export async function listServiceProblems(params?: {
  status?: string;
  companyId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<ServiceProblemListResult> {
  const response = await apiClient.get<ServiceProblemListResult>('/v1/customer-success/problems', {
    params,
  });
  return response.data;
}

export async function listOpenProblemsByCompany(companyId: string): Promise<ServiceProblem[]> {
  const response = await apiClient.get<ServiceProblem[]>(
    `/v1/customer-success/problems/by-company/${companyId}`,
  );
  return response.data;
}

export async function createServiceProblem(
  payload: ServiceProblemUpsertPayload,
): Promise<ServiceProblem> {
  const response = await apiClient.post<ServiceProblem>('/v1/customer-success/problems', payload);
  return response.data;
}

export async function updateServiceProblem(
  id: string,
  payload: ServiceProblemUpsertPayload,
): Promise<ServiceProblem> {
  const response = await apiClient.put<ServiceProblem>(
    `/v1/customer-success/problems/${id}`,
    payload,
  );
  return response.data;
}

export async function deleteServiceProblem(id: string): Promise<void> {
  await apiClient.delete(`/v1/customer-success/problems/${id}`);
}

export async function listProblemLinkedTickets(
  problemId: string,
): Promise<ServiceProblemTicketSummary[]> {
  const response = await apiClient.get<ServiceProblemTicketSummary[]>(
    `/v1/customer-success/problems/${problemId}/tickets`,
  );
  return response.data;
}

export async function linkTicketToProblem(problemId: string, ticketId: string): Promise<void> {
  await apiClient.post(`/v1/customer-success/problems/${problemId}/tickets/${ticketId}`);
}

export async function unlinkTicketFromProblem(problemId: string, ticketId: string): Promise<void> {
  await apiClient.delete(`/v1/customer-success/problems/${problemId}/tickets/${ticketId}`);
}

export type ServiceProblemBroadcastReplyPayload = {
  body?: string;
  useWorkaround?: boolean;
  onlyOpenTickets?: boolean;
  ticketIds?: string[];
};

export type ServiceProblemBroadcastReplyItem = {
  ticketId: string;
  ticketCode: string;
  outcome: string;
  errorMessage?: string | null;
};

export type ServiceProblemBroadcastReplyResult = {
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  items: ServiceProblemBroadcastReplyItem[];
};

export async function broadcastProblemReply(
  problemId: string,
  payload: ServiceProblemBroadcastReplyPayload,
): Promise<ServiceProblemBroadcastReplyResult> {
  const response = await apiClient.post<ServiceProblemBroadcastReplyResult>(
    `/v1/customer-success/problems/${problemId}/broadcast-reply`,
    payload,
  );
  return response.data;
}

export async function suggestProblemBroadcastMessage(
  problemId: string,
  hint?: string,
): Promise<{ text: string; purpose: string }> {
  const response = await apiClient.post<{ text: string; purpose: string }>(
    `/v1/customer-success/problems/${problemId}/suggest-broadcast-message`,
    { hint: hint?.trim() || undefined },
  );
  return response.data;
}
